import { useCallback, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import {
  BackstageIdentityResponse,
  SignInPageProps,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Progress,
  SignInProviderConfig,
  UserIdentity,
} from '@backstage/core-components';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import CotalityDeveloperPortalLogo from '../../assets/images/logo.png';
import { useIdentityStore } from '../../stores/identityStore';
import { ProxiedSignInIdentity } from '../getting-started/ProxiedSignInIdentity';
import { TermsAndConditionsModal } from '../terms-and-conditions/TermsAndConditionsModal';
import { styles } from './SignInPage.styles';
import { useRootStore } from '../../stores/rootStore';
import { isSkilljarHost } from '../../utilities/isSkilljarHost';

type Props = SignInPageProps & {
  provider: SignInProviderConfig;
};

type LoginParams = { checkExisting?: boolean; showPopup?: boolean };

export const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

export const SignInPage = ({ onSignInSuccess, provider }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const country = useRootStore(state => state.country);
  const setProfile = useIdentityStore(state => state.setProfile);
  const setIsGuest = useIdentityStore(state => state.setIsGuest);
  const authApi = useApi(provider.apiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);

  const loginAsGuest = useCallback(() => {
    const guestIdentity = new ProxiedSignInIdentity({
      provider: 'guest',
      discoveryApi,
    });

    if (!localStorage.getItem(PROVIDER_STORAGE_KEY)) {
      localStorage.setItem(PROVIDER_STORAGE_KEY, 'guest');
    }

    setIsGuest(true);
    onSignInSuccess(guestIdentity);
  }, [discoveryApi, onSignInSuccess, setIsGuest]);

  const login = useCallback(
    async ({ checkExisting, showPopup }: LoginParams) => {
      try {
        let identityResponse: BackstageIdentityResponse | undefined;

        if (checkExisting) {
          // for redirecting back to 'company invite link' after logging in
          if (location.pathname.startsWith('/user/register')) {
            sessionStorage.setItem(
              'login-redirect-path',
              location.pathname + location.search,
            );

            if (location.search.includes('isUnregistered=true')) {
              loginAsGuest();
              navigate('/signup');
              return;
            }
          }

          if (localStorage.getItem(PROVIDER_STORAGE_KEY) === 'guest') {
            loginAsGuest();
            return;
          }

          identityResponse = await authApi.getBackstageIdentity({
            optional: true,
          });
        }

        if (!identityResponse && showPopup) {
          setShowLoginPage(true);
          identityResponse = await authApi.getBackstageIdentity({
            instantPopup: true,
          });
        }

        if (!identityResponse) {
          // when redirecting to 'signup page' it should automatically login as guest
          if (location.pathname.startsWith('/signup') || isSkilljarHost) {
            loginAsGuest();
            return;
          }

          setShowLoginPage(true);
          return;
        }

        const profile = await authApi.getProfile();

        setIsGuest(false);
        setProfile(profile);
        onSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi,
            profile,
          }),
        );

        // redirect to 'company invite link' when logged in as a user
        if (sessionStorage.getItem('login-redirect-path')) {
          navigate(sessionStorage.getItem('login-redirect-path')!);
          sessionStorage.removeItem('login-redirect-path');
        }
      } catch (err: any) {
        setShowLoginPage(true);
      }
    },
    [
      authApi,
      location.pathname,
      location.search,
      loginAsGuest,
      navigate,
      onSignInSuccess,
      setIsGuest,
      setProfile,
    ],
  );

  useEffect(() => {
    login({ checkExisting: true });
  }, [login]);

  if (!showLoginPage) {
    return <Progress />;
  }

  return (
    <Stack flex={1} direction="row">
      <Stack justifyContent="center" boxShadow={2} sx={styles.loginContainer}>
        <Box
          component="img"
          src={CotalityDeveloperPortalLogo}
          alt="Cotality"
          height="auto"
          width={360}
          mb={2.5}
        />

        <Typography
          fontSize={24}
          fontFamily="TWK Everett"
          fontWeight={500}
          mb={6}
        >
          Sign in to Cotality's Developer Portal
        </Typography>

        <Stack gap={2}>
          <Button variant="outlined" onClick={loginAsGuest}>
            Continue As Guest
          </Button>

          <Divider flexItem>
            <Typography color="rgba(30, 20, 5, 0.7)" fontSize={12} px={1}>
              OR
            </Typography>
          </Divider>

          <Button
            data-testid="login-button"
            variant="contained"
            onClick={() => login({ showPopup: true })}
          >
            Log In
          </Button>
        </Stack>

        <Typography
          color="rgba(30, 20, 5, 0.7)"
          fontSize={14}
          textAlign="center"
          mt={2}
        >
          Don't have an account?{' '}
          <Link component={RouterLink} to="/signup" onClick={loginAsGuest}>
            Sign up
          </Link>
        </Typography>

        <Typography color="rgba(30, 20, 5, 0.7)" fontSize={14} mt={6}>
          By using Cotality's Developer Portal, you agree to our{' '}
          <Link
            component="button"
            data-testid="terms-and-conditions-link"
            sx={{ verticalAlign: 'baseline' }}
            onClick={() => setShowTermsAndConditions(!showTermsAndConditions)}
          >
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link
            data-testid="privacy-policy-link"
            href={`https://www.cotality.com/${country}/legal/privacy-policy`}
            target="_blank"
          >
            Privacy Policy
          </Link>
          .
        </Typography>
      </Stack>

      <Box flex={1} sx={styles.heroImage} />

      <TermsAndConditionsModal
        open={showTermsAndConditions}
        onClose={() => setShowTermsAndConditions(false)}
      />
    </Stack>
  );
};
